<script setup lang="ts">
import { computed, ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import noImage1to1 from '@/assets/noimage-1to1.jpg';
import {
  COMMENT_SHARE,
  MAX_TOOLTIP_LIST_LENGTH,
  USER_MARK_SHARE,
} from '@/constants';
import { UserInfo } from '@/types';
import { useStore } from '@/utils/vue';

const props = defineProps<{
  action: 'mark' | 'comment';
  users: {
    id: number;
    name: string;
    to: RouteLocationRaw;
  }[];
}>();

const store = useStore();
const activeTeamUsers = computed<UserInfo[]>(
  () => store.getters['teamUsers/activeTeamUsers'],
);

const limit = MAX_TOOLTIP_LIST_LENGTH;
const MAX_ROW_COUNT = 5;

const expanded = ref(false);

const visibleUsers = computed(() =>
  expanded.value ? props.users : props.users.slice(0, limit),
);
const hasOtherUsers = computed(() => props.users.length > limit);
const otherUserCount = computed(() => props.users.length - limit);

const expand = () => {
  expanded.value = true;
};

const actionLabel = computed(() =>
  props.action === 'comment' ? 'コメント' : 'マーク',
);
const emptyText = computed(() =>
  props.action === 'comment' ? COMMENT_SHARE : USER_MARK_SHARE,
);

const profileImageForUser = (userId: number) => {
  const user = activeTeamUsers.value.find(u => u.id === userId);

  return user?.image_url ?? noImage1to1;
};
</script>

<template>
  <template v-if="visibleUsers.length === 0">
    {{ emptyText }}
  </template>
  <div class="list-container" v-else>
    <div class="c-title-bold--xs">{{ actionLabel }}した人</div>
    <div
      class="user-list"
      :style="{ '--rows': Math.min(users.length, MAX_ROW_COUNT) }"
    >
      <template v-for="user in visibleUsers" :key="user.id">
        <router-link :to="user.to" class="c-text-regular--s user-item">
          <img class="profile-image" :src="profileImageForUser(user.id)" />
          {{ user.name }}
        </router-link>
      </template>
    </div>
    <div
      v-if="hasOtherUsers"
      class="c-title-bold--xs"
      :class="{
        clickable: !expanded,
        hidden: expanded,
      }"
      @click.stop="expand"
    >
      {{ `ほか${otherUserCount}人` }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.list-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;

  .c-title-bold--xs {
    color: $color-gray600;
  }

  .user-list {
    display: grid;
    grid-auto-flow: column;
    grid-template-rows: repeat(var(--rows), auto);
    grid-auto-columns: 1fr;
    gap: 4px 12px;

    .user-item {
      display: flex;
      gap: 8px;
      align-items: center;
      word-break: keep-all;
      .profile-image {
        width: 24px;
        height: 24px;
        border-radius: 50%;
      }
    }
  }

  .clickable {
    cursor: pointer;
    text-decoration: underline;
  }

  /**
   * 展開後もスペースを残す。
   * ツールチップの高さが変わると、ホバーから外れて自動で閉じてしまうため。
   */
  .hidden {
    visibility: hidden;
  }
}
</style>
